import { useState, useEffect } from "preact/hooks";
import { useQuery } from "convex/react";
import type { FunctionReference, FunctionArgs, FunctionReturnType } from "convex/server";
import { getCached, setCached } from "../lib/storage";

type UseCachedQueryResult<T> = {
  data: T | null;
  loading: boolean;
};

export function useCachedQuery<Query extends FunctionReference<"query">>(
  query: Query,
  args: FunctionArgs<Query>,
  cacheKey: string,
  editionId: string
): UseCachedQueryResult<FunctionReturnType<Query>> {
  const [cached] = useState(() =>
    getCached<FunctionReturnType<Query>>(cacheKey, editionId)
  );

  const live = useQuery(query, args);

  useEffect(() => {
    if (live !== undefined) {
      setCached(cacheKey, live, editionId);
    }
  }, [live, cacheKey, editionId]);

  return {
    data: (live ?? cached) as FunctionReturnType<Query> | null,
    loading: live === undefined && cached === null,
  };
}
